<script lang="ts">
	import { onMount } from 'svelte'
	import IconBell from '$svg/icon-bell.svg?component'
	import IconCheckmark from '$svg/icon-checkmark.svg?component'
	import IconCross from '$svg/icon-cross.svg?component'
	import { notifications } from '$lib/menubar/mm/menuitems/NotificationsMenuItem/notifications.svelte'
	import { formatIsoDateTime } from '$lib/utils/formatters'
	import Permission from '$lib/menubar/Permission.svelte'
	import { UserPermission } from '$types/enums'
	let {
		hamburgerActive = false,
		context = 'sidebar'
	}: { hamburgerActive?: boolean; context?: 'sidebar' | 'menubar' } = $props()

	const POPOVER_ID = 'user-notifications-popover'
	const ANCHOR = '--user-notifications-bell'

	let popoverOpen = $state(false)

	onMount(() => {
		notifications.start()
		return () => notifications.stop()
	})

	function handleEscape(event: KeyboardEvent) {
		if (event.key !== 'Escape' || !popoverOpen) return
		document.getElementById(POPOVER_ID)?.hidePopover()
		// Drop focus so the bell does not show its keyboard-focus ring after a
		// mouse-driven open + ESC close. Genuine Tab navigation re-focuses normally.
		;(document.activeElement as HTMLElement | null)?.blur()
	}
</script>

<svelte:window onkeydown={handleEscape} />

<Permission requiredPermissions={UserPermission.VIEW_NOTIFICATIONS}>
  {#if !hamburgerActive}
  	<button
  		type="button"
  		class="bell-btn"
  		class:menubar={context === 'menubar'}
  		aria-label={notifications.hasUnread ? 'Benachrichtigungen, ungelesen' : 'Benachrichtigungen'}
  		aria-expanded={popoverOpen}
  		data-active={popoverOpen ? 'true' : undefined}
  		popovertarget={POPOVER_ID}
  		popovertargetaction="toggle"
  		style="anchor-name: {ANCHOR};"
  	>
  		<IconBell class="icon" width="24" height="24" aria-hidden="true" />
  		{#if notifications.hasUnread}
  			<span class="dot" aria-hidden="true"></span>
  		{/if}
  	</button>

  	<div
  		id={POPOVER_ID}
  		popover="auto"
  		class="notifications-popover"
  		class:menubar={context === 'menubar'}
  		style="position-anchor: {ANCHOR};"
  		ontoggle={(event) => {
  			popoverOpen = (event as ToggleEvent).newState === 'open'
  		}}
  	>
  		<button
  			type="button"
  			class="btn-icon-only close-btn"
  			aria-label="Schließen"
  			popovertarget={POPOVER_ID}
  			popovertargetaction="hide"
  		>
  			<IconCross width="12" height="12" aria-hidden="true" />
  		</button>
  		<div class="notifications-list">
  			{#each notifications.items as n (n.id)}
  				<div
  					class={[
  						'entry',
  						(n.type ?? 'unknown').toLowerCase().replace('_', '-'),
  						{ unread: n.state === 'NEW' }
  					]}
  				>
  					<a class="entry-link" href={n.url} onclick={() => notifications.markRead(n.id)}>
  						<span class="icon-slot" aria-hidden="true">
  							{#if n.type === 'OFFER_ACCEPTED'}
  								<IconCheckmark width="16" height="16" />
  							{:else if n.type === 'OFFER_REJECTED'}
  								<IconCross width="16" height="16" />
  							{/if}
  						</span>
  						<span class="body">
  							<span class="text">{n.text}</span>
  							<span class="timestamp">{formatIsoDateTime(n.created)}</span>
  						</span>
  					</a>
  					<button
  						type="button"
  						class="toggle-btn"
  						aria-label={n.state === 'NEW' ? 'Als gelesen markieren' : 'Als ungelesen markieren'}
  						aria-pressed={n.state === 'READ'}
  						onclick={() => notifications.toggleState(n.id)}
  					>
  						<span class="toggle-dot" aria-hidden="true"></span>
  					</button>
  				</div>
  			{:else}
  				<div class="empty">Keine Benachrichtigungen</div>
  			{/each}
  		</div>
  	</div>
  {/if}
</Permission>

<style lang="scss">
	button.bell-btn {
		position: relative;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.25rem;
		background: none;
		border: 0;
		cursor: pointer;
		color: grey;
		transition: color 0.2s ease-in-out;
		border-radius: 50%;

		&:focus-visible {
			outline: 2px solid #3b82f6;
			outline-offset: 2px;
		}

		&:hover,
		&:focus-visible {
			color: #0c0c0c;
		}

		&[data-active='true'] {
			color: #0c0c0c;
			background: rgba(0, 0, 0, 0.05);
		}

		&[aria-expanded='true'] {
			outline: 2px solid black;
			outline-offset: 2px;
		}

		& .dot {
			position: absolute;
			top: 0.25rem;
			right: 0.25rem;
			width: 0.5rem;
			height: 0.5rem;
			background: #d33;
			border-radius: 50%;
		}
	}

	.notifications-popover {
		/* Match profile rail popover: anchor() + fixed (position-area is unreliable in top layer) */
		position: fixed;
		inset: auto;
		bottom: anchor(bottom);
		left: anchor(right);
		margin: 0 0 0 0.5rem;
		padding: 0.25rem 0;
		min-width: 18rem;
		max-width: 24rem;
		overflow: visible;
		background: white;
		border: 1px solid #ddd;
		border-radius: 4px;
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

		&.menubar {
			inset: anchor(bottom) anchor(right) auto auto;
			margin: 0.5rem 0 0 0;
		}

		& .notifications-list {
			max-height: clamp(16rem, 60vh, 600px);
			overflow-y: auto;
			padding: 0.25rem 0;
			border-radius: inherit;
		}

		& .close-btn {
			position: absolute;
			top: 0.25rem;
			right: 0.25rem;
			z-index: 1;
			width: 1.25rem;
			height: 1.25rem;
			color: #888;
			border-radius: 2px;
		}

		/* Bell control is ~32px tall (24px icon + padding); same tail math as ProfileMenu */
		&::before {
			content: '';
			position: absolute;
			top: auto;
			bottom: calc(1rem - 4px);
			left: -5px;
			width: 8px;
			height: 8px;
			background: #fff;
			border-left: 1px solid #ddd;
			border-bottom: 1px solid #ddd;
			pointer-events: none;
			transform: rotate(45deg);
		}

		&.menubar::before {
			inset: -5px 0.75rem auto auto;
			border-left: 1px solid #ddd;
			border-bottom: none;
			border-top: 1px solid #ddd;
			border-right: none;
			transform: rotate(45deg);
		}
	}

	.entry {
		display: flex;
		align-items: stretch;
		width: 100%;
		background: white;

		&:hover {
			background: #f4f4f4;
		}

		& .entry-link {
			display: flex;
			align-items: center;
			gap: 0.5rem;
			flex: 1;
			min-width: 0;
			padding: 0.5rem 0.5rem 0.5rem 0.75rem;
			text-align: left;
			text-decoration: none;
			color: inherit;
			cursor: pointer;

			&:focus-visible {
				outline: 2px solid #3b82f6;
				outline-offset: -2px;
			}
		}

		& .icon-slot {
			display: inline-flex;
			flex-shrink: 0;
			width: 1rem;
			height: 1rem;
		}

		&.offer-accepted .icon-slot {
			color: #1b8a3a;
		}

		&.offer-rejected .icon-slot {
			color: #c92a2a;
		}

		& .body {
			display: flex;
			flex-direction: column;
			gap: 0.125rem;
			min-width: 0;
		}

		& .text {
			font-size: 0.875rem;
			line-height: 1.3;
		}

		& .timestamp {
			font-size: 0.7rem;
			color: #888;
		}

		&.unread .text {
			font-weight: 600;
		}

		& .toggle-btn {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			padding: 0.5rem 0.75rem;
			background: none;
			border: 0;
			cursor: pointer;

			&:hover .toggle-dot,
			&:focus-visible .toggle-dot {
				transform: scale(1.2);
			}

			&:focus-visible {
				outline: 2px solid #3b82f6;
				outline-offset: -2px;
			}
		}

		& .toggle-dot {
			width: 0.5rem;
			height: 0.5rem;
			border-radius: 50%;
			background: #d33;
			transition: transform 0.15s ease-in-out;
		}

		&:not(.unread) .toggle-dot {
			background: transparent;
			border: 1px solid #bbb;
		}
	}

	.empty {
		padding: 0.75rem;
		color: #888;
		font-size: 0.85rem;
	}
</style>
